import { useEffect, useMemo, useState } from 'react';
import { Check, Save } from 'lucide-react';
import {
  DEFAULT_TEMPLATE_ID,
  TEMPLATE_MANIFEST,
  type TemplateId,
} from '@repo/api-client';
import { useActiveOrganization } from '@repo/api-client/react';
import { useUpdateOrganization } from '@repo/queries';
import { Page, PageHeader } from '@/features/common/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert } from '@/components/ui/alert';
import { useToast } from '@/components/ui/use-toast';
import { describeAppError } from '@/features/common/describeAppError';
import { cn } from '@/lib/utils';

const ACCENTS = [
  '#0EA5A5',
  '#C97651',
  '#A87521',
  '#324E68',
  '#1F5230',
  '#7B2D54',
  '#1F6FEB',
  '#0F1320',
] as const;
const DEFAULT_ACCENT = ACCENTS[0];

interface DesignForm {
  template: TemplateId;
  accent: string;
  showLogo: boolean;
  showFooter: boolean;
  logoUrl: string;
}

const EMPTY: DesignForm = {
  template: DEFAULT_TEMPLATE_ID,
  accent: DEFAULT_ACCENT,
  showLogo: true,
  showFooter: true,
  logoUrl: '',
};

function readString(branding: Record<string, unknown> | null | undefined, key: string): string | undefined {
  const v = branding?.[key];
  return typeof v === 'string' ? v : undefined;
}

function readBoolean(
  branding: Record<string, unknown> | null | undefined,
  key: string,
  fallback: boolean,
): boolean {
  const v = branding?.[key];
  return typeof v === 'boolean' ? v : fallback;
}

function isKnownTemplate(raw: string | undefined): raw is TemplateId {
  return TEMPLATE_MANIFEST.some((t) => t.id === raw);
}

/**
 * Org-wide invoice design — template, accent colour, optional logo
 * URL, and the two branding toggles. Persists onto the active org's
 * `branding` JSON via `useUpdateOrganization`. Mirrors the mobile
 * design screen but desktop-native: a side-by-side template grid with
 * inline thumbnails (the SVG manifest already ships with the API
 * client, so no asset pipeline change).
 */
export function DesignScreen() {
  const org = useActiveOrganization();
  const { updateOrganization, loading: saving } = useUpdateOrganization();
  const { toast } = useToast();

  const [form, setForm] = useState<DesignForm>(EMPTY);
  const [snapshot, setSnapshot] = useState<DesignForm>(EMPTY);

  useEffect(() => {
    if (!org) return;
    const branding = (org.branding ?? null) as Record<string, unknown> | null;
    const next: DesignForm = {
      template: isKnownTemplate(readString(branding, 'template'))
        ? (readString(branding, 'template') as TemplateId)
        : DEFAULT_TEMPLATE_ID,
      accent:
        readString(branding, 'accent') ??
        readString(branding, 'primaryColor') ??
        DEFAULT_ACCENT,
      showLogo: readBoolean(branding, 'showLogo', true),
      showFooter: readBoolean(branding, 'showFooter', true),
      logoUrl: org.logoUrl ?? '',
    };
    setForm(next);
    setSnapshot(next);
  }, [org]);

  const dirty = useMemo(
    () => JSON.stringify(form) !== JSON.stringify(snapshot),
    [form, snapshot],
  );

  if (!org) {
    return (
      <Page>
        <PageHeader title="Design" />
        <Skeleton className="h-[60vh] rounded-xl" />
      </Page>
    );
  }

  async function onSave() {
    if (!org) return;
    try {
      await updateOrganization({
        organizationId: org.id,
        updates: {
          logoUrl: form.logoUrl || null,
          branding: {
            template: form.template,
            accent: form.accent,
            primaryColor: form.accent,
            showLogo: form.showLogo,
            showFooter: form.showFooter,
          },
        },
      });
      setSnapshot(form);
      toast({
        title: 'Design saved',
        description: 'New invoices will use this design.',
        variant: 'success',
      });
    } catch (err) {
      const desc = describeAppError(err);
      toast({ title: desc.title, description: desc.message, variant: 'danger' });
    }
  }

  return (
    <Page>
      <PageHeader
        title="Invoice design"
        description="Pick a template, accent and brand mark. Applies to every invoice in this business."
        actions={
          <Button onClick={onSave} loading={saving} disabled={!dirty}>
            <Save className="size-4" /> Save design
          </Button>
        }
      />

      <div className="grid gap-6 lg:grid-cols-[2fr_1fr]">
        <div className="flex flex-col gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Template</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {TEMPLATE_MANIFEST.map((tpl) => {
                  const on = tpl.id === form.template;
                  return (
                    <button
                      key={tpl.id}
                      type="button"
                      onClick={() => setForm((p) => ({ ...p, template: tpl.id }))}
                      className={cn(
                        'flex flex-col items-stretch gap-2 rounded-xl border p-2 text-left transition',
                        on
                          ? 'border-foreground shadow-sm'
                          : 'border-line hover:border-line-strong',
                      )}
                      aria-pressed={on}
                    >
                      <div
                        className="relative aspect-120/170 overflow-hidden rounded-lg bg-secondary"
                        dangerouslySetInnerHTML={{ __html: tpl.thumbnail }}
                      />
                      <div className="flex items-baseline justify-between gap-2 px-1">
                        <span className="text-sm font-medium text-foreground">{tpl.name}</span>
                        {on ? <Check className="size-3.5 text-foreground" /> : null}
                      </div>
                      <p className="px-1 pb-1 text-[11px] leading-tight text-muted-foreground">
                        {tpl.description}
                      </p>
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Accent</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-3">
                {ACCENTS.map((color) => {
                  const on = color === form.accent;
                  return (
                    <button
                      key={color}
                      type="button"
                      aria-label={`Accent ${color}`}
                      aria-pressed={on}
                      onClick={() => setForm((p) => ({ ...p, accent: color }))}
                      className={cn(
                        'relative size-10 rounded-full transition',
                        on
                          ? 'ring-2 ring-offset-2 ring-foreground/60 ring-offset-background'
                          : 'hover:scale-105',
                      )}
                      style={{ backgroundColor: color }}
                    >
                      {on ? (
                        <Check className="absolute inset-0 m-auto size-4 text-white drop-shadow" />
                      ) : null}
                    </button>
                  );
                })}
                <Field label="Custom" htmlFor="customAccent" className="ml-2">
                  <Input
                    id="customAccent"
                    type="color"
                    value={form.accent}
                    onChange={(e) => setForm((p) => ({ ...p, accent: e.target.value }))}
                    className="h-10 w-16 cursor-pointer p-1"
                  />
                </Field>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="flex flex-col gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Brand mark</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-3">
              <div className="flex size-32 items-center justify-center self-start overflow-hidden rounded-xl border border-line bg-secondary/40">
                {form.logoUrl ? (
                  <img
                    src={form.logoUrl}
                    alt="Logo preview"
                    className="size-full object-contain"
                  />
                ) : (
                  <span className="text-xs text-muted-foreground">No logo</span>
                )}
              </div>
              <Field
                label="Logo URL"
                htmlFor="logoUrl"
                hint="Public URL of your logo (PNG or JPEG)."
              >
                <Input
                  id="logoUrl"
                  type="url"
                  value={form.logoUrl}
                  onChange={(e) => setForm((p) => ({ ...p, logoUrl: e.target.value }))}
                  placeholder="https://..."
                />
              </Field>
              {form.logoUrl ? (
                <Alert>
                  Logo uploads via the asset pipeline are coming soon. For now, paste a hosted URL.
                </Alert>
              ) : null}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Branding</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              <ToggleRow
                label="Show logo"
                hint="Top-left of the invoice."
                value={form.showLogo}
                onChange={(v) => setForm((p) => ({ ...p, showLogo: v }))}
              />
              <ToggleRow
                label="Footer message"
                hint="Thank-you note at the bottom."
                value={form.showFooter}
                onChange={(v) => setForm((p) => ({ ...p, showFooter: v }))}
              />
            </CardContent>
          </Card>
        </div>
      </div>
    </Page>
  );
}

function ToggleRow({
  label,
  hint,
  value,
  onChange,
}: {
  label: string;
  hint: string;
  value: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-4">
      <div>
        <div className="text-sm font-medium text-foreground">{label}</div>
        <div className="text-xs text-muted-foreground">{hint}</div>
      </div>
      <Switch checked={value} onCheckedChange={onChange} />
    </div>
  );
}
